import { errorBox } from '@core/components/AlertBox';
import { useThree } from '@react-three/fiber';
import { getTrenchRadiusForViewport } from '@trenches/components/Trench/helper';
import { getCachedMaterial, getRenderOrder } from '@viewer3D/helper';
import { sphereGeometry } from '@viewer3D/helper/geometry-cache';
import { viewer3dSelectors } from '@viewer3D/redux';
import { Object3dNames } from '@viewer3D/types';
import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CurvePath, LineCurve3, Vector3 } from 'three';
import { fetchScanPrelabel } from './api';
import { convertPrelabelFeatures, PrelabelData } from './converter';
import { Point3 } from './ptypes';
import { useScanPrelabelState, setPrelabelScanId, resetScanPrelabelState } from './state';

const PRELABEL_COLOR = '#ff00ff';
const PRELABEL_OPACITY = 0.4;
const PRELABEL_RADIUS_FACTOR = 3;

const PointFeatureMesh: FC<{ coordinate: Point3; radius: number }> = ({ coordinate, radius }) => {
  const position = useMemo(() => new Vector3(...coordinate), [coordinate]);
  const material = getCachedMaterial(PRELABEL_COLOR, PRELABEL_OPACITY);

  const scale = useMemo(() => {
    const s = radius * 1.5;
    return new Vector3(s, s, s);
  }, [radius]);

  return (
    <mesh
      position={position}
      scale={scale}
      geometry={sphereGeometry}
      material={material}
      renderOrder={getRenderOrder(Object3dNames.ScanPrelabel)}
    />
  );
};

const LineFeatureMesh: FC<{ coordinates: Point3[]; radius: number }> = ({
  coordinates,
  radius,
}) => {
  const anchor = useMemo(() => new Vector3(...coordinates[0]), [coordinates]);
  const localPoints = useMemo(() => {
    return coordinates.map(([x, y, z]) => new Vector3(x, y, z).sub(anchor));
  }, [coordinates, anchor]);

  // Construct a single CurvePath composed of LineCurve3 segments to represent the entire line feature.
  // This keeps the linear segments while rendering the path in a single mesh, reducing draw calls.
  const curvePath = useMemo(() => {
    const path = new CurvePath<Vector3>();
    for (let i = 0; i < localPoints.length - 1; i++) {
      path.add(new LineCurve3(localPoints[i], localPoints[i + 1]));
    }
    return path;
  }, [localPoints]);

  const material = getCachedMaterial(PRELABEL_COLOR, PRELABEL_OPACITY);

  // We set the number of tubular segments along the entire tube path.
  // Using a dynamic count proportional to the number of coordinates helps ensure sharp, high-fidelity corners.
  const tubularSegments = useMemo(() => {
    return Math.max(localPoints.length * 4, 32);
  }, [localPoints]);

  return (
    <mesh
      position={anchor}
      material={material}
      renderOrder={getRenderOrder(Object3dNames.ScanPrelabel)}
      frustumCulled={false}
    >
      <tubeGeometry args={[curvePath, tubularSegments, radius, 8, false]} />
    </mesh>
  );
};

export const ScanPrelabel: FC = () => {
  const { show, prelabelScanId } = useScanPrelabelState();
  const selectedScanId = useSelector(viewer3dSelectors.selectedScanId);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const { invalidate } = useThree();
  const [prelabelData, setPrelabelData] = useState<PrelabelData>({ lines: [], points: [] });

  const lineRadius = getTrenchRadiusForViewport(PRELABEL_RADIUS_FACTOR, cameraViewport);

  // Reset prelabel state when project ID changes or component unmounts
  useEffect(() => {
    resetScanPrelabelState();
  }, [projectId]);

  // Synchronize Redux selectedScanId to Valtio prelabelScanId when defined (preserves preview when deselected)
  useEffect(() => {
    if (selectedScanId) {
      setPrelabelScanId(selectedScanId);
    }
  }, [selectedScanId]);

  // Clean up state on unmount to prevent state leakage across different projects/page navigations
  useEffect(() => {
    return () => {
      resetScanPrelabelState();
    };
  }, []);

  const activeScanId = selectedScanId || prelabelScanId;
  const isActive = show && !!activeScanId;

  useEffect(() => {
    if (!isActive || !activeScanId) {
      setPrelabelData({ lines: [], points: [] });
      invalidate();
      return;
    }

    let cancelled = false;

    fetchScanPrelabel(activeScanId)
      .then((prelabel) => {
        if (cancelled) return;
        setPrelabelData(convertPrelabelFeatures(prelabel.features));
        invalidate();
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to fetch scan prelabel', error);
        errorBox('Failed to load scan prelabel');
      });

    return () => {
      cancelled = true;
    };
  }, [isActive, activeScanId, invalidate]);

  const hasContent = prelabelData.lines.length > 0 || prelabelData.points.length > 0;
  if (!isActive || !hasContent) return null;

  return (
    <group name={Object3dNames.ScanPrelabel}>
      {prelabelData.lines.map((feature) => (
        <LineFeatureMesh key={feature.id} coordinates={feature.coordinates} radius={lineRadius} />
      ))}
      {prelabelData.points.map((feature) => (
        <PointFeatureMesh key={feature.id} coordinate={feature.coordinate} radius={lineRadius} />
      ))}
    </group>
  );
};
